// LIBRERIE/IMPORTS
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// COMPONENTI
import Loader from "../Loaders/Loader";

// CONFIGURAZIONI
import appConfig, { DEFAULT_AGENCY_NAME } from "../../config/appConfig";

const LanguageRouteHandler = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isLanguageSet, setIsLanguageSet] = useState(false);

  useEffect(() => {
    // Nota per chi svilupperà dopo di me:
    // Qui gestisco il redirect automatico in base alla lingua.
    // Uso i18n per capire se l'utente ha già una lingua valida nell'URL.
    if (!i18n.isInitialized) {
      // Se i18n non è ancora pronto, non faccio nulla
      return;
    }

    // Se l'integrazione multilingua è attiva (vedi appConfig), controllo la lingua nell'URL
    if (appConfig.integrations.multilang) {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const lng = pathSegments[0];

      // Qui ricavo tutte le lingue supportate
      let supportedLngs: string[] = [];
      if (i18n.options.supportedLngs && Array.isArray(i18n.options.supportedLngs)) {
        supportedLngs = i18n.options.supportedLngs.filter(l => l !== 'cimode') as string[];
      } else if (i18n.options.supportedLngs) {
        // Alcune versioni di i18n usano un oggetto invece di un array
        supportedLngs = Object.keys(i18n.options.supportedLngs).filter(l => l !== 'cimode');
      }

      // Se non ci sono lingue definite, uso quelle di fallback
      if (supportedLngs.length === 0 && i18n.options.fallbackLng) {
        if (typeof i18n.options.fallbackLng === 'string') supportedLngs.push(i18n.options.fallbackLng);
        else if (Array.isArray(i18n.options.fallbackLng)) supportedLngs.push(...i18n.options.fallbackLng as string[]);
        else if (typeof i18n.options.fallbackLng === 'object') supportedLngs.push(...Object.keys(i18n.options.fallbackLng));
      }

      // Prendo la lingua "detected" da i18n (senza regione, es: "en-US" diventa "en")
      const detectedLng = i18n.language.split('-')[0];

      // Se l'URL non ha una lingua o ne ha una non supportata -> faccio redirect a quella giusta
      if (!lng || !supportedLngs.includes(lng)) {
        const newPath = `/${detectedLng}${location.pathname === '/' ? '' : location.pathname}`;
        navigate(newPath + location.search, { replace: true });
        return;
      }

      // Se l'URL ha una lingua diversa da quella attuale di i18n, la cambio
      if (lng !== i18n.language) {
        i18n.changeLanguage(lng).then(() => setIsLanguageSet(true));
      } else {
        setIsLanguageSet(true);
      }
    } else {
      // Se non uso multilingua, setto direttamente il flag
      setIsLanguageSet(true);
    }
  }, [location.pathname, navigate, i18n, i18n.isInitialized, i18n.language]);
  
  // Nota: finché la lingua non è pronta, mostro il loader con il nome agenzia di default
  if (!isLanguageSet || !i18n.isInitialized) {
    return <Loader agencyData={{ agency_name: DEFAULT_AGENCY_NAME }} isLoading={true} />;
  }

  // Quando tutto è pronto, renderizzo i children
  return <>{children}</>;
};

export default LanguageRouteHandler;